import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Badge,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import { countryService } from "../../utils/services/country.service";
import CountryDetailsModal from "../country-details-modal";
import { toast } from "react-toastify";
import SortingIcon from "../../globalStyles/icons/sorting-icon";
import FilterModal from "../filterModal";
import { checkIfFiltered } from "../../utils/utility";
import TableHeader from "../table-header";

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 4,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },

  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const ActionPopover = ({ row, onView, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const openPopover = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const closePopover = () => setAnchorEl(null);

  const doAction = (type) => {
    closePopover();
    if (type === "view") onView(row);
    if (type === "edit") onEdit(row);
    if (type === "delete") onDelete(row);
  };

  return (
    <Box>
      <IconButton
        onClick={openPopover}
        sx={{ color: open ? "primary.main" : "inherit" }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box display="flex" flexDirection="column" p={1}>
          <Button
            onClick={() => doAction("edit")}
            sx={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <EditIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={16}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => doAction("view")}
            sx={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <ViewIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={16}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                View
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => doAction("delete")}
            sx={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <DeleteIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={16}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const CountryList = ({
  data,
  onEditData,
  onDeleteData,
  viewMode,
  selectedCountry,
  setSelectedCountry,
  currentPage,
  itemsPerPage,
  filterData,
  onChangeFilter,
}) => {
  const [rows, setRows] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
  });
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const startIndex = currentPage * itemsPerPage;

  const openDelete = (row) => {
    setSelectedCountry(row);
    setOpenConfirm(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await countryService.delete(selectedCountry._id);
      onDeleteData?.();
      toast.success("Country deleted successfully");
      setOpenConfirm(false);
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const openViewModal = (row) => {
    setSelectedCountry(row);
    setOpenView(true);
  };

  useEffect(() => {
    setRows(
      data?.map((c) => ({
        ...c,
        action: (
          <ActionPopover
            row={c}
            onView={openViewModal}
            onEdit={onEditData}
            onDelete={openDelete}
          />
        ),
      })),
    );
  }, [data]);

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData.columns.find((x) => x.key == filterKey));
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const gridView = () => (
    <Grid
      container
      spacing={2}
      mt={1}
      pb={5}
      sx={{ maxHeight: "100%", overflow: "auto" }}
    >
      {rows?.map((c, i) => (
        <Grid item xs={12} sm={6} md={4} key={c._id}>
          <Card
            sx={{
              border: "1px solid var(--border-color)",
              borderRadius: 1,
              boxShadow: 3,
              height: "100%",
              position: "relative",
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between">
                <Typography
                  fontFamily="Poppins"
                  fontWeight={500}
                  color="var(--text-tertiary)"
                >
                  {startIndex + i + 1}
                </Typography>
                <Box position="absolute" top={10} right={10}>
                  {c.action}
                </Box>
              </Box>

              <Grid container spacing={1} mt={2}>
                <Grid item xs={5}>
                  <Typography
                    fontFamily="Poppins"
                    fontWeight={500}
                    color="var(--text-tertiary)"
                    textAlign={"start"}
                  >
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={c.name}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={500}
                      color="var(--text-tertiary)"
                      noWrap
                      textAlign={"start"}
                    >
                      : {c.name}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    fontFamily="Poppins"
                    fontWeight={500}
                    color="var(--text-tertiary)"
                    textAlign={"start"}
                  >
                    Code2
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={c.code2}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={500}
                      color="var(--text-tertiary)"
                      noWrap
                      textAlign={"start"}
                    >
                      : {c.code2}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    fontFamily="Poppins"
                    fontWeight={500}
                    color="var(--text-tertiary)"
                    textAlign={"start"}
                  >
                    Code3
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={c.code3}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={500}
                      color="var(--text-tertiary)"
                      noWrap
                      textAlign={"start"}
                    >
                      : {c.code3}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="#667085"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "name",
      title: "Name",
      displayKey: "name",
      handleOpenFilter: handleOpenFilter,
    },
    {
      key: "code2",
      title: "Code 2",
      displayKey: "code2",
      handleOpenFilter: handleOpenFilter,
    },
    {
      key: "code3",
      title: "Code 3",
      displayKey: "code3",
      handleOpenFilter: handleOpenFilter,
    },
    {
      key: "currency",
      title: "Currency",
      displayKey: "currency",
      handleOpenFilter: handleOpenFilter,
    },
    { key: "action", title: "", width: "3%", displayKey: "action" },
  ];
  const listView = () => (
    <TableContainer sx={{ overflowY: "auto", maxHeight: "100%" }}>
      <Table stickyHeader sx={{ border: "1px solid var(--border-color)" }}>
        <TableHead>
          <StyledTableRow>
            {TableSchema.map((column) => (
              <TableHeader
                key={column.key}
                column={column}
                filterKey={column.filterKey}
                handleOpenFilter={column.handleOpenFilter}
                title={column.title}
              />
            ))}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows?.map((x, rowIndex) => (
            <StyledTableRow key={x._id}>
              {TableSchema.map((column, colIndex) => (
                <StyledTableCell
                  key={column.key || colIndex}
                  sx={{ width: column.width }}
                >
                  {column.displayKey ? (
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {x[column.displayKey] || "--"}
                    </Typography>
                  ) : column.render ? (
                    column.render(x, rowIndex)
                  ) : (
                    "--"
                  )}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgcolor="rgba(0,0,0,0.5)"
          zIndex={9999}
          color="#fff"
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <Box
          sx={{
            overflowY: "scroll",
            display: "flex",
            maxHeight: isMobile() ? "60vh" : "calc(100vh - 210px)",
            width: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { width: 0 },
          }}
        >
          {viewMode === "grid" ? gridView() : listView()}
        </Box>
      )}

      <DeleteConfirmationModal
        title={`Are you sure you want to delete ${selectedCountry?.name} ?`}
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleDelete={confirmDelete}
      />

      <CountryDetailsModal
        country={selectedCountry}
        open={openView}
        onClose={() => setOpenView(false)}
      />
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedfilterData?.options}
          selectedValues={selectedfilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedfilterData?.sort}
          filterKey={selectedfilterData?.key}
          hasMore={selectedfilterData?.loadMoreButton}
          displayKey={selectedfilterData?.displayKey}
          id={selectedfilterData?.id}
          fetchApi={selectedfilterData?.fetchApi}
          searchKey={selectedfilterData?.searchKey}
        />
      </Popover>
    </Box>
  );
};

export default CountryList;
